import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, Chip, Paper,
  CircularProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LagbasLogo from '../../../../components/LagbasLogo';
import HeroBg from '../components/HeroBg';
import { db } from '../../../../firebase/config';
import {
  collection, doc, getDoc, getDocs, query, orderBy,
  onSnapshot, writeBatch,
} from 'firebase/firestore';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Toaster } from 'sonner';
import { MAPPN_ACCENT, STATUS_OPTIONS, TECH_TYPES } from '../constants';
import ComponentDetail from '../components/ComponentDetail';

function estLabel(low, high) {
  if (!low && !high) return null;
  if (low === high || !high) return `${low} h`;
  if (!low) return `${high} h`;
  return `${low}–${high} h`;
}

function compEst(comp) {
  if (comp.estimateLow || comp.estimateHigh) return { low: comp.estimateLow || 0, high: comp.estimateHigh || 0 };
  const low  = (comp.features || []).reduce((s, f) => s + (Number(f.estimateLow)  || 0), 0);
  const high = (comp.features || []).reduce((s, f) => s + (Number(f.estimateHigh) || 0), 0);
  return { low, high };
}

function colEst(comps) {
  let low = 0, high = 0;
  for (const c of comps) { const e = compEst(c); low += e.low; high += e.high; }
  return { low, high };
}

function colTech(comps) {
  const seen = new Set();
  const result = [];
  for (const c of comps) {
    for (const t of (c.techStack || [])) {
      if (!seen.has(t.name) && t.name) { seen.add(t.name); result.push(t); }
    }
  }
  return result;
}

// ── Sortable card ────────────────────────────────────────────────────────────

function SortableCard({ comp, categories, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: comp.id });

  const status  = STATUS_OPTIONS.find(s => s.key === comp.status);
  const cat     = categories.find(c => c.id === comp.categoryId);
  const est     = compEst(comp);
  const estStr  = estLabel(est.low, est.high);

  return (
    <Paper
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        bgcolor: isDragging ? '#1e2940' : '#1a1f2e',
        border: `1px solid ${isDragging ? MAPPN_ACCENT + '44' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 1.5, p: 1.5, mb: 1,
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? `0 4px 20px rgba(0,0,0,0.5)` : 'none',
        display: 'flex', alignItems: 'flex-start', gap: 0.75,
        cursor: 'default',
      }}
    >
      <Box {...attributes} {...listeners}
        sx={{ color: 'rgba(255,255,255,0.15)', cursor: 'grab', mt: 0.25, flexShrink: 0 }}>
        <DragIndicatorIcon sx={{ fontSize: '0.9rem' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }} onClick={onClick} style={{ cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
          {comp.avatarUrl && (
            <Box component="img" src={comp.avatarUrl}
              sx={{ width: 20, height: 20, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }} />
          )}
          {status && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
          )}
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 700, color: 'white',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {comp.title || '(Namnlös)'}
          </Typography>
        </Box>

        {comp.description && (
          <Typography sx={{
            fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            mb: 0.5,
          }}>
            {comp.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {cat && (
            <Chip label={cat.name} size="small" sx={{
              bgcolor: cat.color + '22', color: cat.color,
              fontSize: '0.6rem', height: 15, fontWeight: 700,
            }} />
          )}
          {estStr && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AccessTimeIcon sx={{ fontSize: '0.65rem', color: MAPPN_ACCENT + 'aa' }} />
              <Typography sx={{ fontSize: '0.65rem', color: MAPPN_ACCENT + 'aa' }}>{estStr}</Typography>
            </Box>
          )}
          {(comp.features || []).length > 0 && (
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)' }}>
              {comp.features.length} funk.
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

// ── Perspective column ───────────────────────────────────────────────────────

function PerspectiveColumn({ perspective, comps, categories, caseId, caseTechStack, perspectives, onOpenComp }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [items, setItems] = useState(comps);

  useEffect(() => { setItems(comps); }, [comps]);

  const est   = colEst(items);
  const estStr = estLabel(est.low, est.high);
  const tech  = colTech(items);

  async function onDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const from = items.findIndex(c => c.id === active.id);
    const to   = items.findIndex(c => c.id === over.id);
    const next = arrayMove(items, from, to);
    setItems(next);

    const batch = writeBatch(db);
    next.forEach((comp, idx) => {
      batch.update(doc(db, 'mappnCases', caseId, 'components', comp.id), {
        [`priorityByPerspective.${perspective.id}`]: idx + 1,
      });
    });
    await batch.commit();
  }

  return (
    <Box sx={{
      width: 280, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Column header */}
      <Box sx={{
        bgcolor: '#161b22',
        border: `1px solid ${perspective.color}33`,
        borderRadius: '10px 10px 0 0',
        p: 1.5, mb: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: perspective.color, flexShrink: 0 }} />
          <Typography fontWeight={800} sx={{ color: 'white', fontSize: '0.9rem', flex: 1 }}>
            {perspective.name}
          </Typography>
          <Chip
            label={items.length}
            size="small"
            sx={{ bgcolor: perspective.color + '22', color: perspective.color, fontSize: '0.65rem', height: 18, fontWeight: 700 }}
          />
        </Box>

        {estStr && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)' }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
              {estStr} totalt
            </Typography>
          </Box>
        )}

        {tech.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
            {tech.slice(0, 4).map(t => {
              const tt = TECH_TYPES.find(x => x.key === t.type) || TECH_TYPES[7];
              return (
                <Chip key={t.name} label={t.name} size="small" sx={{
                  bgcolor: tt.bg + '33', color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.58rem', height: 14,
                }} />
              );
            })}
            {tech.length > 4 && (
              <Chip label={`+${tech.length - 4}`} size="small" sx={{
                bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)',
                fontSize: '0.58rem', height: 14,
              }} />
            )}
          </Box>
        )}
      </Box>

      {/* Cards */}
      <Box sx={{
        flex: 1, p: 1.5, pt: 1,
        bgcolor: '#0d1117',
        border: `1px solid ${perspective.color}22`,
        borderTop: 'none',
        borderRadius: '0 0 10px 10px',
        minHeight: 80,
      }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map(c => c.id)} strategy={verticalListSortingStrategy}>
            {items.map(comp => (
              <SortableCard
                key={comp.id}
                comp={comp}
                categories={categories}
                onClick={() => onOpenComp(comp)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {items.length === 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', py: 2 }}>
            Inga komponenter
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function MappNPerspectives() {
  const { caseId } = useParams();
  const navigate   = useNavigate();

  const [caseData, setCaseData]         = useState(null);
  const [perspectives, setPerspectives] = useState([]);
  const [categories, setCategories]     = useState([]);
  const [components, setComponents]     = useState([]);
  const [loading, setLoading]           = useState(true);

  const [detailOpen, setDetailOpen]   = useState(false);
  const [detailId, setDetailId]       = useState(null);
  const [detailData, setDetailData]   = useState(null);

  useEffect(() => {
    let unsub;
    async function init() {
      setLoading(true);
      const [cSnap, pSnap, catSnap] = await Promise.all([
        getDoc(doc(db, 'mappnCases', caseId)),
        getDocs(query(collection(db, 'mappnCases', caseId, 'perspectives'), orderBy('order'))),
        getDocs(query(collection(db, 'mappnCases', caseId, 'categories'), orderBy('order'))),
      ]);
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      setCaseData({ id: cSnap.id, ...cSnap.data() });
      setPerspectives(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      unsub = onSnapshot(
        query(collection(db, 'mappnCases', caseId, 'components'), orderBy('createdAt')),
        snap => { setComponents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }
      );
    }
    init();
    return () => unsub?.();
  }, [caseId, navigate]);

  const openEdit = useCallback((comp) => {
    setDetailId(comp.id);
    setDetailData(comp);
    setDetailOpen(true);
  }, []);

  function handleSaved(savedId, savedData) {
    setComponents(prev =>
      prev.map(c => c.id === savedId ? { ...c, ...savedData } : c)
    );
  }

  function handleDeleted(deletedId) {
    setComponents(prev => prev.filter(c => c.id !== deletedId));
    setDetailOpen(false);
  }

  const TABS = [
    { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
  ];

  if (loading && !caseData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: MAPPN_ACCENT }} />
      </Box>
    );
  }

  const unassigned = components.filter(c => !(c.perspectiveIds || []).length);
  const hasHero = !!(caseData?.heroVideoUrl || caseData?.heroImageUrl);

  return (
    <>
    <HeroBg caseData={caseData} />
    <Box sx={{ minHeight: '100vh', bgcolor: hasHero ? 'transparent' : '#0d1117', color: 'white', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
      <Toaster richColors position="top-center" />

      {/* Header */}
      <Box sx={{
        bgcolor:        hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
        backdropFilter: hasHero ? 'blur(22px) saturate(1.3)' : 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10, flexShrink: 0,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {caseData?.heroImageUrl && (
          <Box component="img" src={caseData.heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{caseData?.name || '…'}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {perspectives.length} perspektiv · {components.length} komponenter
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {TABS.map(tab => (
            <Button
              key={tab.path}
              startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color: tab.path === 'perspectives' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'perspectives' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1,
                '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Columns */}
      <Box sx={{ flex: 1, overflowX: 'auto', p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: MAPPN_ACCENT }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: 'max-content' }}>
            {perspectives.map(persp => {
              const comps = components
                .filter(c => (c.perspectiveIds || []).includes(persp.id))
                .sort((a, b) => {
                  const pa = a.priorityByPerspective?.[persp.id] ?? 9999;
                  const pb = b.priorityByPerspective?.[persp.id] ?? 9999;
                  return pa - pb;
                });
              return (
                <PerspectiveColumn
                  key={persp.id}
                  perspective={persp}
                  comps={comps}
                  categories={categories}
                  caseId={caseId}
                  caseTechStack={caseData?.techStack || []}
                  perspectives={perspectives}
                  onOpenComp={openEdit}
                />
              );
            })}

            {/* Unassigned column */}
            {unassigned.length > 0 && (
              <Box sx={{ width: 280, flexShrink: 0 }}>
                <Box sx={{
                  bgcolor: '#161b22',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px 10px 0 0',
                  p: 1.5,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#555', flexShrink: 0 }} />
                    <Typography fontWeight={700} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', flex: 1 }}>
                      Inget perspektiv
                    </Typography>
                    <Chip label={unassigned.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#666', fontSize: '0.65rem', height: 18 }} />
                  </Box>
                </Box>
                <Box sx={{
                  p: 1.5, pt: 1, bgcolor: '#0d1117',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderTop: 'none', borderRadius: '0 0 10px 10px',
                }}>
                  {unassigned.map(comp => {
                    const status = STATUS_OPTIONS.find(s => s.key === comp.status);
                    const cat    = categories.find(c => c.id === comp.categoryId);
                    return (
                      <Paper
                        key={comp.id}
                        onClick={() => openEdit(comp)}
                        sx={{
                          bgcolor: '#1a1f2e', border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: 1.5, p: 1.5, mb: 1, cursor: 'pointer',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.15)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                          {status && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />}
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {comp.title || '(Namnlös)'}
                          </Typography>
                        </Box>
                        {cat && <Chip label={cat.name} size="small" sx={{ bgcolor: cat.color + '22', color: cat.color, fontSize: '0.6rem', height: 15 }} />}
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}

            {perspectives.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 10, color: 'rgba(255,255,255,0.3)', width: '100%' }}>
                <Typography sx={{ mb: 1 }}>Inga perspektiv skapade</Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/applab/mappn/${caseId}/settings`)}
                  sx={{ borderColor: MAPPN_ACCENT, color: MAPPN_ACCENT, textTransform: 'none' }}
                >
                  Skapa perspektiv i Inställningar
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ComponentDetail
        open={detailOpen}
        caseId={caseId}
        componentId={detailId}
        initialData={detailData}
        perspectives={perspectives}
        categories={categories}
        caseTechStack={caseData?.techStack || []}
        onClose={() => setDetailOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </Box>
    </>
  );
}
